import React, { useCallback } from 'react';
import { observer } from 'mobx-react-lite';

import { fixTotalValue } from '@trader/utils';
import { useMst } from '@trader/store';
import { useI18next } from '@trader/services';

import * as Styled from './styled';
import { shouldDisplayCreditInPortfolio } from '@trader/constants';

interface IBalanceDetails {
  isShowDetails: boolean;
}

export const BalanceDetails: React.FC<IBalanceDetails> = React.memo(
  ({ isShowDetails }) => {
    const store = useMst();
    const { translate } = useI18next();

    const portfolio = store.user.portfolio;

    const handleFixed = useCallback(
      (value: number): string => {
        if (!value) {
          return '--';
        }
        return `${portfolio.currencySymbol}${value.toFixed(fixTotalValue)}`;
      },
      [portfolio.currencySymbol]
    );

    return (
      <Styled.Root $isShowDetails={isShowDetails}>
        <Styled.BalanceContainer>
          <Styled.Title>{translate('COMMON.LABELS.BALANCE')}</Styled.Title>
          <Balance onFixed={handleFixed} />
        </Styled.BalanceContainer>

        {shouldDisplayCreditInPortfolio && (
          <Styled.BalanceContainer>
            <Styled.Title>{translate('COMMON.LABELS.CREDIT')}</Styled.Title>
            <Credit onFixed={handleFixed} />
          </Styled.BalanceContainer>
        )}

        <Styled.BalanceContainer>
          <Styled.Title>{translate('COMMON.LABELS.EQUITY')}</Styled.Title>
          <Equity onFixed={handleFixed} />
        </Styled.BalanceContainer>

        <Styled.BalanceContainer>
          <Styled.Title>{translate('COMMON.LABELS.FREE_MARGIN')}</Styled.Title>
          <FreeMargin onFixed={handleFixed} />
        </Styled.BalanceContainer>

        <Styled.BalanceContainer>
          <Styled.Title>{translate('COMMON.LABELS.P_L')}</Styled.Title>
          <Pl onFixed={handleFixed} />
        </Styled.BalanceContainer>

        <Styled.BalanceContainer id='marginLevel'>
          <Styled.Title>{translate('COMMON.LABELS.MARGIN_LEVEL')}</Styled.Title>
          <MarginLevel />
        </Styled.BalanceContainer>

        <Styled.BalanceContainer>
          <Styled.Title>{translate('COMMON.LABELS.USED_MARGIN')}</Styled.Title>
          <UsedMargin onFixed={handleFixed} />
        </Styled.BalanceContainer>
      </Styled.Root>
    );
  }
);

interface IDetail {
  onFixed: (value: number) => string;
}

const Balance: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return (
    <Styled.BalanceContainer>
      <Styled.Value>{onFixed(portfolio.balance) || '--'}</Styled.Value>
    </Styled.BalanceContainer>
  );
});

const Credit: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return <Styled.Value>{onFixed(portfolio.credit) || '--'}</Styled.Value>;
});

const Equity: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return <Styled.Value>{onFixed(portfolio.equity) || '--'}</Styled.Value>;
});

const FreeMargin: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return <Styled.Value>{onFixed(portfolio.freeMargin) || '--'}</Styled.Value>;
});

const Pl: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return <Styled.Value>{onFixed(portfolio.pl) || '--'}</Styled.Value>;
});

const MarginLevel: React.FC = observer(() => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return (
    <Styled.Value>
      {portfolio.marginLevel
        ? `${portfolio.marginLevel.toFixed(fixTotalValue)}%`
        : '--'}
    </Styled.Value>
  );
});

const UsedMargin: React.FC<IDetail> = observer(({ onFixed }) => {
  const store = useMst();
  const portfolio = store.user.portfolio;

  return <Styled.Value>{onFixed(portfolio.usedMargin) || '--'}</Styled.Value>;
});
